import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { UserAnalyticsService } from "@analytics/index";

import industries from "@components/FilterControls/utilities/Industries";

import { CompanyType } from "@interfaces/company.type";

import generateCompanyColumns from "@pages/Companies/configs/generateCompanyColumns";
import { addIndustryToFilter, handleRowFocus } from "@pages/Companies/utils";

import { ICompanyTableControls } from "@store/filters/filter.types";
import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";

interface ICompaniesTableConfig {
  companies: CompanyType[];
  setFocusedId: (id: string) => void;
}

const useCompaniesTableConfig = ({ companies, setFocusedId }: ICompaniesTableConfig) => {
  const { filters, pagination, selection, setTableFilters, sort } = useTableStore("company");

  const oldFiltersObject = useRef<ICompanyTableControls>();

  useEffect(() => {
    if (oldFiltersObject.current) {
      UserAnalyticsService.trackCompanyFilter(
        {
          filters,
          pagination,
          selection,
          sort,
        },
        oldFiltersObject.current,
        "smart-search",
      );
    }
    oldFiltersObject.current = {
      filters,
      pagination,
      selection,
      sort,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleAddIndustryToFilter = useCallback(
    (newIndustry: string) => {
      addIndustryToFilter({
        filtersObject: {
          filters,
          pagination,
          selection,
          sort,
        },
        handleSetFilters: setTableFilters,
        industries,
        newIndustry,
      });
      PopupActions.openAlert({
        duration: 1000,
        message: "Filter Applied",
        severity: "success",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, setTableFilters],
  );

  const columns = generateCompanyColumns({
    addIndustryToFilter: handleAddIndustryToFilter,
    filtersObject: {
      filters,
      pagination,
      selection,
      sort,
    },
  });

  const rows = useMemo(
    () =>
      companies.map((company) => {
        if (!company) {
          return {
            country: null,
            id: 1,
            key: 1,
            logo: null,
            name: null,
            numEmployees: null,
            primaryDomain: null,
            sector: null,
          };
        }

        return {
          country: company.country || "-",
          id: company.id,
          key: company.id,
          logo: { alt: company.name || "", url: company.logoUrl || "" },
          name: company.name || "-",
          numEmployees: company.estimatedNumEmployees ?? "-",
          primaryDomain: company.primaryDomain || "-",
          sector: _.uniq(company.industries),
        };
      }),
    [companies],
  );

  const initialState = {
    pagination: {
      paginationModel: { page: pagination.page, pageSize: pagination.pageSize },
    },
  };

  return {
    columns,
    handleRowFocus: handleRowFocus({ companies, setFocusedId }),
    initialState,
    rows,
  };
};

export default useCompaniesTableConfig;
